import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Autocomplete, Button, Grid, Stack, TextField } from "@mui/material";
import { GridColumnMenu } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import moment from "moment";

import Modal from "Components/Common/Modal";
import CommentInputForReason from "Components/Common/CommentInputForReason";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import Backdrop from "Components/Common/Backdrop";

import { getImageUrl } from "utils";
import { formatUtilityAddress } from "utils/formatUtilityAddress";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import {
  useLazyGetBulkOtherUtilityTechnicalSanctionQuery,
  useLazyGetBulkTechnicalSanctionQuery,
  usePutBulkTechnicalSanctionDocumentApproveMutation,
} from "apis/TechnicalSanction";

import { StyledGrid, StyledIconButton } from "./style";

const technicalSanctionOptions = [
  { value: true, label: "Approve" },
  { value: false, label: "Revoke" },
];

const CustomColumnMenu = (props) => (
  <GridColumnMenu
    {...props}
    slots={{
      columnMenuColumnsItem: null,
      columnMenuSortItem: null,
    }}
  />
);

const TechnicalSanctionActionModal = ({ handleClose, isPowerUtility, t }) => {
  const [
    approveTechnicalSanction,
    { isLoading: isTechnicalSanctionApproving },
  ] = usePutBulkTechnicalSanctionDocumentApproveMutation();

  const [
    getPowerUtilityApplicationsData,
    {
      isLoading: isPowerUtilityApplicationDataLoading,
      data: { data: powerUtilityTechnicalSanctionsData } = {},
    },
  ] = useLazyGetBulkTechnicalSanctionQuery();

  const [
    getOtherUtilityApplicationsData,
    {
      isLoading: isOtherUtilityApplicationDataLoading,
      data: { data: otherUtilityTechnicalSanctionsData } = {},
    },
  ] = useLazyGetBulkOtherUtilityTechnicalSanctionQuery();

  const [technicalSanctionIds, setTechnicalSanctionIds] = useState([]);
  const [isRevokeButtonClicked, setIsRevokeButtonClicked] = useState(false);
  const [isApproveButtonClicked, setIsApproveButtonClicked] = useState(false);
  const [documentToPreview, setDocumentToPreview] = useState(null);
  const [selectedTechnicalSanctionAction, setSelectedTechnicalSanctionAction] =
    useState(true);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const technicalSanctionsData = useMemo(
    () =>
      powerUtilityTechnicalSanctionsData ??
      otherUtilityTechnicalSanctionsData ??
      [],
    [powerUtilityTechnicalSanctionsData, otherUtilityTechnicalSanctionsData]
  );
  const columns = useMemo(() => {
    return [
      {
        headerName: t("Application Number"),
        field: "applicationNumber",
        width: 150,
      },
      {
        headerName: t("Application Type"),
        field: "applicationType",
        width: 200,
        valueGetter: ({ row: { applicationType: { name } = {} } = {} }) => name,
      },
      {
        headerName: t("Name"),
        field: "name",
        width: 150,
        valueGetter: ({ row: { name } = {} }) => name,
      },
      {
        headerName: t("Utility Address"),
        field: "utilityAddress",
        width: 250,
        valueGetter: ({ row: { utilityAddress } = {} }) => {
          const address = formatUtilityAddress(utilityAddress);
          if (address) {
            return `${address}, Gandhinagar-382050`;
          }
          return "--";
        },
      },
      {
        headerName: t("Date"),
        field: "date",
        width: 150,
        valueGetter: ({ row: { submissionDate } = {} }) =>
          moment(submissionDate).isValid()
            ? convertUTCDateToLocalDate(submissionDate).format("DD-MM-YYYY")
            : "N/A",
      },
      {
        headerName: t("Status"),
        field: "status",
        width: 200,
        valueGetter: ({ row: { applicationStatus: { name } = {} } }) => name,
      },
      {
        headerName: t("Action"),
        field: "action",
        width: 150,
        renderCell: ({ row: { technicalSanctionUrl } = {} }) => {
          return (
            <StyledIconButton
              onClick={() => {
                setDocumentToPreview({
                  url: getImageUrl(technicalSanctionUrl),
                  documentTypeName: "Technical Sanction Document",
                  documentType: "pdf",
                  documentName: "technicalSanction",
                });
              }}
            >
              <VisibilityIcon
                fontSize="small"
                color="primary"
                className="cursor-pointer"
              />
            </StyledIconButton>
          );
        },
      },
    ];
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (isPowerUtility) {
          await getPowerUtilityApplicationsData({
            isPendingTechnicalSanctionApprovals: true,
            isApproved: selectedTechnicalSanctionAction,
          });
        } else {
          await getOtherUtilityApplicationsData({
            isPendingTechnicalSanctionApprovals: true,
            isApproved: selectedTechnicalSanctionAction,
          });
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [selectedTechnicalSanctionAction]);

  const submitHandlerForApproval = async () => {
    try {
      await approveTechnicalSanction({
        applicationIds: technicalSanctionIds,
        isApproved: true,
      });
      setIsApproveButtonClicked(false);
      setTechnicalSanctionIds([]);
    } catch (err) {}
  };

  const submitHandlerForRevoked = async (comment) => {
    try {
      await approveTechnicalSanction({
        applicationIds: technicalSanctionIds,
        isApproved: false,
        comment,
      });
      setIsRevokeButtonClicked(false);
      setTechnicalSanctionIds([]);
    } catch (err) {}
  };

  return (
    <>
      <Backdrop
        open={
          isPowerUtilityApplicationDataLoading ||
          isOtherUtilityApplicationDataLoading
        }
      />

      {isApproveButtonClicked && (
        <ConfirmationDialog
          message="Are you sure you want to approve Technical Sanctions?"
          onCancel={() => setIsApproveButtonClicked(false)}
          onConfirm={submitHandlerForApproval}
          isOpen={isApproveButtonClicked}
          title="Warning"
          variant="warning"
          isLoading={isTechnicalSanctionApproving}
        />
      )}

      {isRevokeButtonClicked && (
        <CommentInputForReason
          handleClose={() => {
            setIsRevokeButtonClicked(false);
          }}
          handleSubmit={submitHandlerForRevoked}
          isOpen={isRevokeButtonClicked}
          isLoading={isTechnicalSanctionApproving}
          title="Add Reason for Revoked"
          errorMessage="Please enter reason for revoked"
        />
      )}

      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}

      <Modal
        isOpen
        handleClose={handleClose}
        title={t("Technical Sanctions Approval")}
        maxWidth="lg"
      >
        <Grid container mt={2}>
          <Grid item xs={12} sm={6} md={4} xl={3}>
            <Autocomplete
              size="small"
              options={technicalSanctionOptions}
              renderInput={(params) => (
                <TextField {...params} label={t("Select Action")} />
              )}
              value={technicalSanctionOptions.find(
                (action) => action.value === selectedTechnicalSanctionAction
              )}
              onChange={(_, { value }) => {
                setTechnicalSanctionIds([]);
                setSelectedTechnicalSanctionAction(value);
              }}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.value}>
                    {option.label}
                  </li>
                );
              }}
              disableClearable
            />
          </Grid>
        </Grid>

        <StyledGrid
          className="mt-2"
          checkboxSelection
          autoHeight
          disableRowSelectionOnClick
          rows={technicalSanctionsData || []}
          columns={columns}
          pageSizeOptions={[10, 50, 100]}
          rowCount={technicalSanctionsData?.length}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          getRowId={(row) => row.id}
          onRowSelectionModelChange={(ids) => {
            // eslint-disable-next-line no-undef
            const selectedIDs = new Set(ids);
            setTechnicalSanctionIds(Array.from(selectedIDs));
          }}
          slots={{
            columnMenu: CustomColumnMenu,
            noRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {t("No records found")}.
              </Stack>
            ),
          }}
          localeText={{
            footerRowSelected: () => {
              const technicalSanctionIdsLength = technicalSanctionIds.length;
              if (technicalSanctionIdsLength) {
                return `${technicalSanctionIdsLength} ${
                  technicalSanctionIdsLength === 1 ? "row" : "rows"
                } selected`;
              }
            },
          }}
          rowSelection
          rowSelectionModel={technicalSanctionIds}
          keepNonExistentRowsSelected
        />

        <div className="text-end">
          {selectedTechnicalSanctionAction && (
            <Button
              variant="contained"
              className="mt-2"
              disabled={!technicalSanctionIds.length}
              sx={{ mr: 2 }}
              color="success"
              onClick={() => setIsApproveButtonClicked(true)}
            >
              {t("Approve")}
            </Button>
          )}

          {!selectedTechnicalSanctionAction && (
            <Button
              variant="contained"
              className="mt-2 me-2"
              disabled={!technicalSanctionIds.length}
              color="warning"
              onClick={() => setIsRevokeButtonClicked(true)}
            >
              {t("Revoke")}
            </Button>
          )}
        </div>
      </Modal>
    </>
  );
};

TechnicalSanctionActionModal.propTypes = {
  handleClose: PropTypes.func,
  isPowerUtility: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(TechnicalSanctionActionModal);
